/* eslint-disable max-lines */
import {
    memo,
    useCallback,
    useMemo,
    type ReactElement,
    type ReactNode,
} from 'react';

import type { SortingState } from '@buying/library';

import { useIsLargeScreen } from '@bestseller-bit/frontend-community.theme.buying-theme';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { SortingIcon } from './SortingIcon';

interface TableLabelSorting {
    /** The sorting id for this column */
    sortingId: SortingState['id'];
    /** The current sorting state */
    currentSorting: SortingState | null;
    /** Toggles sorting for the given sortingId */
    toggleSorting: (sortingId: SortingState['id']) => void;
}

interface TableLabelProps {
    /** The main title of the table label */
    mainTitle?: string;
    /** Optional sub title. It will be visually above the main title */
    subTitle?: string;
    /** Sorting id + state + toggle handler. Provide to enable sorting for this column. */
    sorting?: TableLabelSorting;
    /** Vertical alignment of the content within the table label, will make it align to the bottom. Will align no matter what if subtitle is defined @default false */
    bottomAligned?: boolean;
    /** Horizontal alignment of the content within the table label, will make it align to the right @default false */
    rightAligned?: boolean;
    /** Reverse order of main title and sub title @default false */
    reverseOrder?: boolean;
    /** Whether to wrap texts or not @default false */
    wrapTexts?: boolean;
    /** Optional tooltip content shown on hover */
    tooltip?: ReactNode;
}

/**
 * A shareable table label component with sorting capabilities.
 * @requires SortingProvider - Must be wrapped in SortingProvider for sorting to work
 * @requires ThemeProvider - Needs Material-UI theme context
 */
const TableLabel = memo(
    ({
        mainTitle,
        subTitle,
        sorting,
        bottomAligned = false,
        rightAligned = false,
        reverseOrder = false,
        wrapTexts = false,
        tooltip,
    }: TableLabelProps): ReactElement => {
        const { palette } = useTheme();
        const sortingId = sorting?.sortingId;
        const currentSorting = sorting?.currentSorting ?? null;
        const isLargeScreen = useIsLargeScreen();

        // If the currentSorting.id is equal to the sortingId then return the direction
        const isSorted = useMemo(() => {
            if (!currentSorting || !sortingId) return false;
            return currentSorting.id === sortingId && currentSorting.direction;
        }, [currentSorting, sortingId]);

        const shouldAlignToBottom = !!subTitle;

        const horizontalAlignment = rightAligned ? 'flex-end' : 'flex-start';

        const textAlignment = rightAligned ? 'right' : undefined;

        const tooltipPlacement = rightAligned ? 'bottom-end' : 'bottom-start';

        // Toggling only works when a sorting object (id + handlers) is provided
        const handleSorting = useCallback(() => {
            if (!sorting) return;

            sorting.toggleSorting(sorting.sortingId);
        }, [sorting]);

        const iconSize = isLargeScreen ? 20 : 16;

        return (
            <Tooltip
                title={tooltip ?? ''}
                placement={tooltipPlacement}
                disableHoverListener={!tooltip}
                disableFocusListener={!tooltip}
                disableTouchListener={!tooltip}
                slotProps={{
                    popper: {
                        modifiers: [
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, 0], // Default is -8px, a little too close to the label
                                },
                            },
                        ],
                    },
                }}
            >
                <Stack
                    sx={{
                        flexGrow: 1,
                        justifyContent:
                            shouldAlignToBottom || bottomAligned
                                ? 'flex-end'
                                : 'center',
                        alignItems: horizontalAlignment,
                    }}
                >
                    <Stack
                        onClick={(): void => {
                            handleSorting();
                        }}
                        onKeyDown={(e): void => {
                            if (e.key === 'Enter') {
                                handleSorting();
                            }
                        }}
                        {...(sortingId
                            ? {
                                  tabIndex: 0,
                                  role: 'button',
                                  sx: {
                                      cursor: 'pointer',
                                      // A little ux improvement to make the hitbox larger
                                      position: 'relative',

                                      ['&::before']: {
                                          content: '""',
                                          position: 'absolute',
                                          inset: -10,
                                      },

                                      ['&:hover, &:focus']: {
                                          ...(!isSorted && {
                                              ['& .sort-icon']: {
                                                  color: `${alpha(palette.primary.main, 0.5)} !important`,
                                              },
                                          }),
                                          ...(rightAligned && {
                                              ['& .main-title-wrapper']: {
                                                  paddingRight: `${iconSize + 4}px`, // Icon size + Spacing
                                              },
                                          }),
                                      },

                                      ...(rightAligned &&
                                          isSorted && {
                                              ['& .main-title-wrapper']: {
                                                  paddingRight: `${iconSize + 4}px`, // Icon size + Spacing
                                              },
                                          }),
                                  },
                              }
                            : {})}
                    >
                        <Stack
                            className="main-title-wrapper"
                            direction="row"
                            sx={{
                                alignItems: 'center',

                                // Needed for hover effect if rightAligned
                                transition: 'padding-right 0.18s ease-out',
                            }}
                        >
                            <Stack
                                spacing={0.5}
                                sx={{
                                    ...(reverseOrder
                                        ? { flexDirection: 'column-reverse' }
                                        : {}),
                                }}
                                useFlexGap
                            >
                                {subTitle && (
                                    <Typography
                                        variant="buttons.xsmall"
                                        sx={{
                                            color: alpha(
                                                palette.primary.main,
                                                0.5
                                            ),
                                            ...(wrapTexts
                                                ? { textWrap: 'balance' }
                                                : { whiteSpace: 'nowrap' }),
                                            ...(textAlignment && {
                                                textAlign: textAlignment,
                                            }),
                                        }}
                                    >
                                        {subTitle}
                                    </Typography>
                                )}

                                {mainTitle && (
                                    <Typography
                                        variant={
                                            isLargeScreen
                                                ? 'headings.h4'
                                                : 'buttons.small'
                                        }
                                        sx={{
                                            textTransform: 'uppercase',
                                            ...(wrapTexts
                                                ? { textWrap: 'balance' }
                                                : { whiteSpace: 'nowrap' }),
                                            ...(rightAligned && {
                                                textAlign: 'right',
                                                flexGrow: 1,
                                            }),
                                        }}
                                    >
                                        {mainTitle}
                                    </Typography>
                                )}
                            </Stack>

                            {sortingId && (
                                <SortingIcon
                                    sorting={isSorted}
                                    iconSize={iconSize}
                                    rightAligned={rightAligned}
                                />
                            )}
                        </Stack>
                    </Stack>
                </Stack>
            </Tooltip>
        );
    }
);

TableLabel.displayName = 'TableLabel';

export { TableLabel };
export type { TableLabelSorting };
